import {META_DEFAULT_BASE_URL} from '../constants';

/**
 * The languages the body of `/communities` is translated into, and the bundle each
 * one reads.
 *
 * The route renders in one of these regardless of how many locales `[lang]` accepts:
 * the header and footer follow the requested locale, but the page body only has
 * translations for these three. Any other `[lang]` falls back to English — see
 * `resolveCommunitiesLang`. The i18n provider, the `lang`/`dir` subtree markers, and
 * the route metadata all read from here so they cannot disagree.
 *
 * Deliberately free of `server-only` and of any filesystem import, unlike its sibling
 * `communities-locale.ts`: `communities-i18n-provider.tsx` is a client component and
 * needs these same values, so they cannot live in a server-only module.
 *
 * Adding a language is: one entry here, plus its `[lang]` → locale pair already being
 * known to `@udemy-v2/nextjs-core`'s `i18nPathToBundleMap`.
 */
export const COMMUNITIES_LOCALES = {
    en: 'en_US',
    es: 'es_ES',
    pt: 'pt_BR',
} as const;

export type CommunitiesLang = keyof typeof COMMUNITIES_LOCALES;

export const DEFAULT_COMMUNITIES_LANG: CommunitiesLang = 'en';

export const COMMUNITIES_LANGS = Object.keys(COMMUNITIES_LOCALES) as CommunitiesLang[];

export function isCommunitiesLang(lang: string): lang is CommunitiesLang {
    return Object.prototype.hasOwnProperty.call(COMMUNITIES_LOCALES, lang);
}

/** Maps any `[lang]` segment onto the language the page body will actually render in. */
export function resolveCommunitiesLang(lang: string): CommunitiesLang {
    return isCommunitiesLang(lang) ? lang : DEFAULT_COMMUNITIES_LANG;
}

/**
 * The canonical URL of the route in a given supported language.
 *
 * ASSUMPTION TO VERIFY: that English lives at `/communities/` with no locale prefix
 * and the others at `/{lang}/communities/`, matching the rest of udemy.com. If the
 * edge routing serves English at `/en/communities/` instead, this function is the only
 * thing that changes.
 */
export function getCommunitiesUrl(lang: CommunitiesLang): string {
    return lang === DEFAULT_COMMUNITIES_LANG
        ? `${META_DEFAULT_BASE_URL}/communities/`
        : `${META_DEFAULT_BASE_URL}/${lang}/communities/`;
}

/**
 * `hreflang` alternates for the supported languages, plus `x-default` pointing at
 * English. Emitted only on the supported-language pages; an unsupported `[lang]` serves
 * the English body and canonicalizes to the English URL instead, so it is deliberately
 * left out of the set.
 */
export function getCommunitiesAlternates(): Record<string, string> {
    return {
        ...Object.fromEntries(COMMUNITIES_LANGS.map((lang) => [lang, getCommunitiesUrl(lang)])),
        'x-default': getCommunitiesUrl(DEFAULT_COMMUNITIES_LANG),
    };
}
